import { __assign } from "tslib";
import { isNil } from "@w11k/rx-ninja";
import { enableMapSet, enablePatches } from "immer";
import { createStore } from "redux";
import { Observable, ReplaySubject } from "rxjs";
import { checkDevModeAndCreateDevToolsEnabledComposeFn, isTyduxDevelopmentModeEnabled } from "./development";
import { getDeep, setDeep } from "./utils";
function throwErrorOnInvalidSliceNamePath(sliceNamePath) {
    // TODO: check all characters instead of only ' '
    if (sliceNamePath.indexOf(" ") !== -1) {
        throw new Error("sliceNamePath contains whitespace");
    }
}
var TyduxStore = /** @class */ (function () {
    function TyduxStore(store, facadeReducers) {
        this.store = store;
        this.facadeReducers = facadeReducers;
        this.knownSlicePaths = {};
    }
    TyduxStore.prototype.getState = function () {
        return this.store.getState();
    };
    TyduxStore.prototype.select = function () {
        var _this = this;
        return new Observable(function (observer) {
            observer.next(_this.store.getState());
            return _this.store.subscribe(function () {
                Promise.resolve().then(function () { return observer.next(_this.store.getState()); });
            });
        });
    };
    TyduxStore.prototype.internalCreateMountPoint = function (stateGetter, stateSetter, freeSlicePath) {
        var _this = this;
        return {
            tyduxStore: this,
            addReducer: function (commandReducer) { return _this.facadeReducers.push(commandReducer); },
            dispatch: function (action) { return _this.store.dispatch(action); },
            getState: function () { return stateGetter(_this.store.getState()); },
            extractState: function (state) { return stateGetter(state); },
            setState: stateSetter,
            subscribe: this.store.subscribe.bind(this.store),
            freeSlicePath: freeSlicePath,
            destroySubject: new ReplaySubject(1),
        };
    };
    TyduxStore.prototype.registerSlicePath = function (path) {
        if (this.knownSlicePaths[path]) {
            throw new Error("slice path already in use");
        }
        this.knownSlicePaths[path] = true;
    };
    TyduxStore.prototype.createMountPoint = function (sliceName) {
        var _this = this;
        this.registerSlicePath(sliceName);
        return (__assign({ sliceName: sliceName.toString() }, this.internalCreateMountPoint(function (s) { return s[sliceName]; }, function (s, l) {
            var state = Object.assign({}, s);
            state[sliceName] = l;
            return state;
        }, function () { return _this.knownSlicePaths[sliceName] = false; })));
    };
    TyduxStore.prototype.createDeepMountPoint = function (sliceNamePath) {
        var _this = this;
        if (isTyduxDevelopmentModeEnabled()) {
            throwErrorOnInvalidSliceNamePath(sliceNamePath);
        }
        this.registerSlicePath(sliceNamePath);
        return (__assign({ sliceName: sliceNamePath }, this.internalCreateMountPoint(function (s) { return getDeep(s, sliceNamePath); }, function (s, l) {
            return setDeep(s, sliceNamePath, l);
        }, function () { return _this.knownSlicePaths[sliceNamePath] = false; })));
    };
    return TyduxStore;
}());
export { TyduxStore };
var TyduxReducerBridge = /** @class */ (function () {
    function TyduxReducerBridge() {
        this.facadeReducers = [];
    }
    TyduxReducerBridge.prototype.createTyduxReducer = function (initialState) {
        var _this = this;
        var firstCall = true;
        return function (state, action) {
            if (firstCall) {
                state = state === undefined ? initialState : state;
                firstCall = false;
            }
            for (var _i = 0, _a = _this.facadeReducers; _i < _a.length; _i++) {
                var reducer = _a[_i];
                state = reducer(state, action);
            }
            return state;
        };
    };
    TyduxReducerBridge.prototype.wrapReducer = function (wrappedReducer) {
        var tyduxReducer = this.createTyduxReducer(undefined);
        return function (state, action) {
            var stateAfterWrappedReducer = wrappedReducer(state, action);
            return tyduxReducer(stateAfterWrappedReducer, action);
        };
    };
    TyduxReducerBridge.prototype.connectStore = function (store) {
        return new TyduxStore(store, this.facadeReducers);
    };
    return TyduxReducerBridge;
}());
export { TyduxReducerBridge };
export function createTyduxStore(initialState, config) {
    if (initialState === void 0) { initialState = {}; }
    if (config === void 0) { config = {}; }
    enableMapSet();
    enablePatches();
    var bridge = new TyduxReducerBridge();
    var rootReducer = isNil(config.reducer)
        ? bridge.createTyduxReducer(initialState)
        : bridge.wrapReducer(config.reducer);
    var enhancer = checkDevModeAndCreateDevToolsEnabledComposeFn({
        name: config ? config.name : undefined
    });
    var reduxStore = createStore /*cast due to strange TS error*/(rootReducer, initialState, config.enhancer ? enhancer(config.enhancer) : enhancer());
    return bridge.connectStore(reduxStore);
}
